import { Button, Checkbox, FormControl, FormLabel, Input, Stack } from '@chakra-ui/react';
import { yupResolver } from '@hookform/resolvers/yup';
import { useEffect } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import { useTranslations } from 'use-intl';

import { noop } from '@blockpulse3/data/shared';
import { ErrorMessage, useErrorToast, useSuccessToast } from '@blockpulse3/ui/commons';

import { useGetCompanyQuery, useSetCompanyContactMutation } from '@blockpulse3/graphql/hooks';
import { useParams } from 'react-router-dom';
import { naturalAccessFormDefaults, naturalAccessFormSchema } from './schema';
import { INaturalCompanyAccessForm } from './types';

type Props = {
  /* ** Disabled fields ** */
  isDisabled?: boolean;
  /* ** Is editing mode ** */
  isEditing?: boolean;
  /* ** Default form values ** */
  defaultValues: INaturalCompanyAccessForm;
  /* ** Should activate 'Delete' button ** */
  shouldCancel?: boolean;
  /* ** Callback on delete button ** */
  onCancel?: () => void;
  /* ** Callback on submit button ** */
  onSubmit?: (data: INaturalCompanyAccessForm) => void;
};

export function NaturalCompanyAccessForm({
  isDisabled = false,
  isEditing = false,
  defaultValues = naturalAccessFormDefaults,
  shouldCancel = true,
  onCancel = noop,
  onSubmit = noop,
}: Props): JSX.Element {
  const i18nGeneral = useTranslations('General');
  const i18nNaturalOtherRoleForm = useTranslations('NaturalOtherRoleForm');

  const errorToast = useErrorToast();
  const successToast = useSuccessToast();

  const { companyId = '' } = useParams();
  const { refetch } = useGetCompanyQuery({
    variables: { companyId },
    skip: !companyId,
  });

  const [setCompanyContact] = useSetCompanyContactMutation();

  const { register, formState, setValue, handleSubmit } = useForm<INaturalCompanyAccessForm>({
    defaultValues,
    resolver: yupResolver(naturalAccessFormSchema),
  });

  const handleFormSubmit: SubmitHandler<INaturalCompanyAccessForm> = (data) => {
    onSubmit(data);
  };

  const handleFormCancel = (): void => {
    onCancel();
  };

  const handleSetCompanyContact = (e: React.ChangeEvent<HTMLInputElement>, id: string): void => {
    if (id) {
      setCompanyContact({
        variables: {
          setCompanyContactInput: {
            representativeId: id,
            companyId,
            isContact: e.target.checked,
          },
        },
        onCompleted: () => {
          refetch();
          successToast({ title: i18nNaturalOtherRoleForm('Toast.ContactSuccess') });
        },
        onError: () => {
          errorToast({ title: i18nNaturalOtherRoleForm('Toast.ContactError') });
        },
      });
    }
  };

  useEffect(() => {
    /* ** Fill fields on default values change: switching the it's me switch ** */
    setValue('firstName', defaultValues.firstName);
    setValue('lastName', defaultValues.lastName);
  }, [defaultValues, setValue]);

  return (
    <form onSubmit={handleSubmit(handleFormSubmit)}>
      <Stack spacing="8">
        <fieldset disabled={isDisabled}>
          <Stack spacing="4">
            <FormControl isInvalid={!!formState.errors?.email}>
              <FormLabel htmlFor="email">{i18nNaturalOtherRoleForm('EmailField')}</FormLabel>
              <Input id="email" type="text" {...register('email')} />
              <ErrorMessage error={formState.errors?.email} />
            </FormControl>
            <Stack direction={{ base: 'column', md: 'row' }} spacing="4">
              <FormControl isInvalid={!!formState.errors?.firstName}>
                <FormLabel htmlFor="firstName">
                  {i18nNaturalOtherRoleForm('FirstNameField')}
                </FormLabel>
                <Input id="firstName" type="text" {...register('firstName')} />
                <ErrorMessage error={formState.errors?.firstName} />
              </FormControl>
              <FormControl isInvalid={!!formState.errors?.lastName}>
                <FormLabel htmlFor="lastName">
                  {i18nNaturalOtherRoleForm('LastNameField')}
                </FormLabel>
                <Input id="name" type="text" {...register('lastName')} />
                <ErrorMessage error={formState.errors?.lastName} />
              </FormControl>
            </Stack>
          </Stack>
        </fieldset>
        {isEditing && (
          <Stack direction={{ base: 'column', md: 'row' }} spacing="4">
            <Checkbox
              isChecked={defaultValues.isContact}
              onChange={(e): void => handleSetCompanyContact(e, defaultValues.id || '')}
            >
              {i18nNaturalOtherRoleForm('ContactField')}
            </Checkbox>
          </Stack>
        )}
        <Stack direction={{ base: 'column', md: 'row' }} spacing="4">
          <Button
            isDisabled={!shouldCancel}
            type="button"
            variant="secondary"
            width="full"
            onClick={handleFormCancel}
          >
            {i18nGeneral('Delete')}
          </Button>
          <Button isDisabled={isDisabled} type="submit" width="full">
            {i18nGeneral('Submit')}
          </Button>
        </Stack>
      </Stack>
    </form>
  );
}

export type NaturalCompanyAccessFormProps = Props;
